/*
 * THE REST OF WHAT THE PERSON IS TYPING — grey text after the caret, taken with Tab.
 *
 * Two sources, cheapest first. When the draft is the start of the suggested next reply, the rest of
 * that reply is already known and shows at once: the presenter types the first words of the line
 * the panel offered and the remainder follows them, at no cost. Otherwise, once the person pauses,
 * a small model is asked to continue what they typed (`ai-worker` complete.ts).
 *
 * The grey text belongs to ONE draft: shown only while the box holds exactly the text it was made
 * for. Typing the characters it predicted consumes it from the front; typing anything else drops it
 * and, after the next pause, asks again. A reply that arrives after the box has moved on is dropped
 * — the same rule as the suggested next reply, for the same reason.
 *
 * Drawn by the box's mark layer (mentions.ts `renderMentionBackdrop`), which already lies under the
 * textarea with the same text, font and wrapping: grey text appended there sits exactly after the
 * last character typed.
 */
/** How long the person pauses before the model is asked. */
export const PAUSE_MS = 400;
/** Fewer characters than this is too little to continue from. */
const MIN_CHARS = 3;
/** The rest of the offered line when the draft is its start (ignoring case), or ''. */
export function ghostFromLine(draft, line) {
    if (!line || !draft || draft.length >= line.length)
        return '';
    return line.slice(0, draft.length).toLowerCase() === draft.toLowerCase() ? line.slice(draft.length) : '';
}
/** What is left of the grey text once `typed` was added after its draft: the part not yet typed, or
 *  '' when the person went another way. */
export function consumeGhost(ghost, typed) {
    return typed && ghost.toLowerCase().startsWith(typed.toLowerCase()) ? ghost.slice(typed.length) : '';
}
/** Whether a draft is worth asking the model to continue. A command is the list's, not the model's. */
export function wantsCompletion(draft, s) {
    if (!s.atEnd || s.composing || s.listOpen || s.busy)
        return false;
    const start = draft.trim();
    return start.length >= MIN_CHARS && !start.startsWith('/') && !draft.endsWith('\n');
}
/** Tab takes the grey text only as a bare key, and only while some is on show. */
export function ghostTakesTab(e, ghost) {
    if (e.key !== 'Tab' || e.shiftKey || e.altKey || e.ctrlKey || e.metaKey)
        return false;
    return ghost !== '';
}
const timer = (fn, ms) => {
    const id = setTimeout(fn, ms);
    return () => clearTimeout(id);
};
export class LiveCompletion {
    constructor(opts) {
        this.opts = opts;
        this.draft = '';
        this.ghost = '';
        this.cancel = null;
        /** Bumped by every edit, so a reply asked for an older draft knows it is stale. */
        this.seq = 0;
    }
    /** The grey text for this draft, or '' — never one made for another. */
    ghostFor(draft) {
        return draft === this.draft ? this.ghost : '';
    }
    /** After every edit to the box. */
    update(draft, s) {
        // Typed what the grey text said: it shrinks from the front, and nothing is asked.
        if (this.ghost && s.atEnd && draft.length > this.draft.length && draft.startsWith(this.draft)) {
            const rest = consumeGhost(this.ghost, draft.slice(this.draft.length));
            if (rest)
                return this.show(draft, rest);
        }
        this.stop();
        const fromLine = s.atEnd && !s.composing && !s.busy ? ghostFromLine(draft, this.opts.line()) : '';
        this.show(draft, fromLine);
        if (fromLine || !wantsCompletion(draft, s))
            return;
        const seq = this.seq;
        this.cancel = (this.opts.schedule ?? timer)(() => {
            this.cancel = null;
            void this.ask(draft, seq);
        }, PAUSE_MS);
    }
    /** Taken with Tab, or dismissed (Escape, a caret moved away, the box left): gone until the next edit. */
    clear() {
        this.stop();
        this.show('', '');
    }
    async ask(draft, seq) {
        const out = await this.opts.complete(draft).catch(() => '');
        if (seq !== this.seq || !out)
            return; // the box moved on while the model was thinking
        this.show(draft, out);
    }
    stop() {
        this.cancel?.();
        this.cancel = null;
        this.seq++;
    }
    show(draft, ghost) {
        if (draft === this.draft && ghost === this.ghost)
            return;
        this.draft = draft;
        this.ghost = ghost;
        this.opts.changed();
    }
}
//# sourceMappingURL=live-completion.js.map